import { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Award, Printer, TrendingUp, TrendingDown, Minus, X } from "lucide-react";
import {
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  LabelList,
} from "recharts";

const GRADE_COLOR = {
  A: "#16a34a", B: "#65a30d", C: "#ca8a04",
  D: "#ea580c", E: "#dc2626", F: "#7f1d1d",
};
const GRADE_NUM = { A: 6, B: 5, C: 4, D: 3, E: 2, F: 1 };
const NUM_GRADE = { 6: "A", 5: "B", 4: "C", 3: "D", 2: "E", 1: "F" };
const TERM_COLORS = [
  "#97bc62", "#3d5a80", "#82a0bc", "#e07a5f", "#65a30d", "#7c3aed",
  "#0891b2", "#ea580c", "#16a34a", "#dc2626", "#0284c7", "#a16207",
  "#22c55e", "#6d28d9", "#14b8a6", "#c2410c", "#84cc16", "#9333ea",
];
const fmtTerm = (t) => (t || "").replace(/(VT|HT)(\d{4})/, "$1 $2").replace("20", "");

function termSortKey(t) {
  const m = /^(VT|HT)(\d{4})$/.exec(t || "");
  if (!m) return 9999;
  return parseInt(m[2], 10) + (m[1] === "HT" ? 0.5 : 0);
}

/**
 * Utskriftsvänlig betygssida. Visar:
 *  - Meritvärde-kort (officiellt + eget uträknat)
 *  - Betygs-tabell (alla terminer, ärvda i blått)
 *
 * Hide för utskrift: navigation, knappar, edit-läge.
 * Tar valfri ?child=<user_id> query-param för att förvälja barn.
 */
export default function GradesPrintPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const childParam = params.get("child");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeIdx, setActiveIdx] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/vklass/grades");
        setData(r.data || null);
        if (childParam && r.data?.children) {
          const idx = r.data.children.findIndex((c) => c.user_id === childParam);
          if (idx >= 0) setActiveIdx(idx);
        }
      } finally { setLoading(false); }
    })();
  }, [childParam]);

  const child = data?.children?.[activeIdx] || data?.children?.[0] || null;
  const officialMap = child?.meritvarde_official || {};
  const computedMap = child?.meritvarde_computed || {};

  const subjects = useMemo(() => child?.subjects || [], [child]);
  const realTerms = useMemo(() => {
    const s = new Set(data?.terms || []);
    Object.keys(officialMap).forEach((t) => s.add(t));
    Object.keys(computedMap).forEach((t) => s.add(t));
    return Array.from(s).sort((a, b) => termSortKey(a) - termSortKey(b));
  }, [data, officialMap, computedMap]);

  // Demo-läge: simulera fler terminer för visuell validering av utskriftslayout.
  // Använd ?demo_terms=11 → genererar 11 fejk-terminer med slumpade betyg.
  const demoTerms = parseInt(params.get("demo_terms") || "0", 10);
  const { terms, demoSubjects } = useMemo(() => {
    if (!demoTerms || demoTerms <= realTerms.length) {
      return { terms: realTerms, demoSubjects: subjects };
    }
    // Bygg N terminer bakåt från senaste verkliga termin
    const baseTerms = realTerms.length > 0 ? realTerms : ["VT2026"];
    const last = baseTerms[baseTerms.length - 1];
    const m = /^(VT|HT)(\d{4})$/.exec(last) || ["", "VT", "2026"];
    let prefix = m[1];
    let year = parseInt(m[2], 10);
    const generated = [...baseTerms];
    while (generated.length < demoTerms) {
      // Gå framåt i tid
      if (prefix === "VT") { prefix = "HT"; }
      else { prefix = "VT"; year += 1; }
      generated.push(`${prefix}${year}`);
    }
    const gradesPool = ["A", "B", "C", "D", "E"];
    const fakeSubs = subjects.map((s, sIdx) => {
      const newGrades = { ...(s.grades || {}) };
      const newInh = { ...(s.inherited_by_term || {}) };
      // Fyll på deterministiska "fejk"-betyg för nya terminer (för förutsägbar layout-validering)
      for (let i = 0; i < generated.length; i++) {
        const t = generated[i];
        if (!newGrades[t]) {
          // Deterministisk val baserat på subject-index + term-index
          newGrades[t] = gradesPool[(sIdx * 3 + i * 2) % gradesPool.length];
          // Var fjärde är "ärvt"
          if ((sIdx + i) % 4 === 0) newInh[t] = true;
        }
      }
      return { ...s, grades: newGrades, inherited_by_term: newInh };
    });
    return { terms: generated, demoSubjects: fakeSubs };
  }, [demoTerms, realTerms, subjects]);

  // Trend mellan terminer (höjda/sänkta/oförändrade per parställning)
  const trendData = useMemo(() => {
    const out = [];
    for (let i = 1; i < terms.length; i++) {
      const prevT = terms[i - 1];
      const newT = terms[i];
      let up = 0, down = 0, same = 0;
      const upList = [], downList = [];
      for (const s of demoSubjects) {
        const a = (s.grades || {})[prevT];
        const b = (s.grades || {})[newT];
        if (!a || !b) continue;
        const an = GRADE_NUM[a];
        const bn = GRADE_NUM[b];
        if (an == null || bn == null) continue;
        if (bn > an) { up += 1; upList.push(`${s.name} ${a}→${b}`); }
        else if (bn < an) { down += 1; downList.push(`${s.name} ${a}→${b}`); }
        else same += 1;
      }
      if (up + down + same > 0) {
        out.push({ from: prevT, to: newT, up, down, same, upList, downList });
      }
    }
    return out;
  }, [terms, demoSubjects]);

  // Data för stapeldiagram — Y = ämnen, X = betygsvärde
  // För saknade terminer: använd sentinel-värdet 6 (max) med en lätt grå
  // overlay i Cell — så slot:en visuellt fylls och raderna blir uniformt
  // höga oavsett om första terminen saknas eller inte.
  const chartData = useMemo(() => {
    return demoSubjects.map((s) => {
      const row = { name: s.name };
      for (const t of terms) {
        const g = (s.grades || {})[t];
        if (g) {
          row[t] = GRADE_NUM[g];
          row[`${t}_label`] = `${fmtTerm(t)} ${g}`;
          row[`${t}_present`] = true;
        } else {
          row[t] = 6; // full bredd så slot:en syns men markerad som missing
          row[`${t}_label`] = "";
          row[`${t}_present`] = false;
        }
      }
      return row;
    });
  }, [terms, demoSubjects]);

  const officialTerms = terms.filter((t) => officialMap[t] != null);
  const latestOfficialTerm = officialTerms[officialTerms.length - 1] || null;
  const officialValue = latestOfficialTerm ? officialMap[latestOfficialTerm] : null;

  if (loading) {
    return <div className="p-6 text-sm text-gray-500">Hämtar betyg…</div>;
  }
  if (!child) {
    return (
      <div className="p-6 max-w-3xl mx-auto">
        <button
          onClick={() => navigate("/meddelanden")}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gray-100 text-xs font-medium text-gray-700 mb-4"
        >
          <X size={14} /> Tillbaka till meddelanden
        </button>
        <p className="text-sm">Inga betyg.</p>
      </div>
    );
  }

  const closeAndGoBack = () => {
    // Stäng om vi öppnades via window.open (push deeplink öppnar typiskt
    // i nytt fönster eller fokuserar PWA-instans), annars navigera tillbaka
    // till meddelandena.
    if (window.opener && !window.opener.closed) {
      window.close();
      return;
    }
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate("/meddelanden");
    }
  };

  return (
    <div className="max-w-3xl mx-auto p-6 print:p-2 print:max-w-none" data-testid="grades-print-page">
      <style>{`
        @media print {
          @page { size: A4; margin: 12mm; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .no-print { display: none !important; }
          .card-print { border: 1px solid #d1d5db !important; box-shadow: none !important; page-break-inside: avoid; }
          .page-break-before { page-break-before: always; break-before: page; }
        }
      `}</style>

      {/* Sticky close-bar — alltid synlig så användaren kan komma tillbaka.
          Göms helt vid utskrift via .no-print. */}
      <div className="no-print sticky top-0 z-20 -mx-6 px-6 py-2 bg-white border-b border-gray-200 mb-4 flex items-center justify-between gap-2">
        <button
          onClick={closeAndGoBack}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gray-100 hover:bg-gray-200 text-xs font-medium text-gray-700"
          data-testid="grades-print-close-btn"
          aria-label="Stäng och gå tillbaka"
        >
          <X size={14} />
          Stäng
        </button>
        <span className="text-[11px] uppercase tracking-wider text-gray-500">
          Betygsöversikt
        </span>
      </div>

      {/* Print-knapp + barn-väljare (göms på print) */}
      <div className="no-print mb-4 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-wider text-gray-500">Utskrift</p>
          <h1 className="text-xl font-medium flex items-center gap-2 mt-0.5">
            <Award size={20} className="text-amber-500" />
            Betyg — {child.name}
          </h1>
        </div>
        <div className="flex gap-2 items-center">
          {data.children.length > 1 && (
            <select
              value={activeIdx}
              onChange={(e) => setActiveIdx(parseInt(e.target.value, 10))}
              className="px-3 py-2 rounded-full border text-xs"
              data-testid="grades-print-child-select"
            >
              {data.children.map((c, i) => (
                <option key={c.user_id} value={i}>{c.name}</option>
              ))}
            </select>
          )}
          <button
            onClick={() => window.print()}
            className="px-3 py-2 rounded-full bg-emerald-700 text-white text-xs font-medium inline-flex items-center gap-1.5"
            data-testid="grades-print-btn"
          >
            <Printer size={12} /> Skriv ut
          </button>
        </div>
      </div>

      {/* Print-header (visas BARA vid utskrift) */}
      <div className="hidden print:block mb-3">
        <p className="text-xs uppercase tracking-wider text-gray-500">Betygsöversikt</p>
        <h1 className="text-lg font-medium">{child.name}</h1>
      </div>

      {/* Demo-banner — visas både på skärm och utskrift för transparens */}
      {demoTerms > 0 && demoTerms > realTerms.length && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-2 mb-3 text-[10px] text-amber-900" data-testid="grades-print-demo-banner">
          <strong>DEMO-LÄGE:</strong> Visar {terms.length} terminer ({demoTerms - realTerms.length} simulerade
          med slumpade betyg) för att förhandsgranska utskriftslayout. Ta bort <code>?demo_terms=</code> ur URL:en
          för att se de verkliga betygen.
        </div>
      )}

      {/* Meritvärde-kort */}
      <div className="rounded-xl border border-gray-200 bg-white p-4 mb-4 card-print" data-testid="meritvarde-card-print">
        <p className="text-xs uppercase tracking-wider text-gray-500 mb-1">
          Meritvärde {officialValue != null && `· ${fmtTerm(latestOfficialTerm)}`}
        </p>
        {officialValue != null ? (
          <>
            <p className="text-4xl font-medium">
              {officialValue.toFixed(1)}
              <span className="text-base text-gray-400 ml-1">/ 340</span>
            </p>
            <p className="text-xs text-gray-600 mt-1">
              Vklass officiella slutbetyg från {fmtTerm(latestOfficialTerm)}.
            </p>
          </>
        ) : (
          <p className="text-2xl font-medium text-gray-500">Inget officiellt meritvärde</p>
        )}
      </div>

      {/* Betygs-tabell */}
      <div className="rounded-xl border border-gray-200 bg-white p-3 mb-4 card-print" data-testid="grades-print-table">
        <p className="text-xs uppercase tracking-wider text-gray-500 mb-3">Betyg över terminer</p>
        <div className="grid grid-cols-[120px_1fr] gap-x-2 text-xs">
          <div></div>
          <div className="grid gap-1" style={{ gridTemplateColumns: `repeat(${terms.length}, 1fr)` }}>
            {terms.map((t) => (
              <div key={t} className="text-center text-[10px] text-gray-500">
                {fmtTerm(t)}
              </div>
            ))}
          </div>
          {demoSubjects.map((s) => {
            const inh = s.inherited_by_term || {};
            return (
              <div key={`${s.code}-${s.name}`} className="contents">
                <div className="font-medium text-gray-700 py-1 truncate" title={s.name}>{s.name}</div>
                <div className="grid gap-1 py-1" style={{ gridTemplateColumns: `repeat(${terms.length}, 1fr)` }}>
                  {terms.map((t) => {
                    const g = (s.grades || {})[t];
                    const inherited = !!inh[t];
                    if (!g) {
                      return <div key={t} className="text-center text-gray-300">–</div>;
                    }
                    if (inherited) {
                      return (
                        <div
                          key={t}
                          className="text-center font-bold rounded text-blue-800 bg-blue-100 border border-blue-300 text-[11px] py-0.5"
                          title="Ärvt från föregående termin"
                        >
                          ({g})
                        </div>
                      );
                    }
                    return (
                      <div
                        key={t}
                        className="text-center font-bold rounded text-white text-[11px] py-0.5"
                        style={{ backgroundColor: GRADE_COLOR[g] || "#999" }}
                      >
                        {g}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {/* Officiellt meritvärde-rad */}
          <div className="font-medium text-gray-700 py-1 border-t border-gray-200 mt-1 pt-2">Meritvärde</div>
          <div
            className="grid gap-1 py-1 border-t border-gray-200 mt-1 pt-2"
            style={{ gridTemplateColumns: `repeat(${terms.length}, 1fr)` }}
          >
            {terms.map((t) => {
              const v = officialMap[t];
              return (
                <div key={t} className="text-center text-[10px] text-gray-700">
                  {v != null ? v.toFixed(1) : "–"}
                </div>
              );
            })}
          </div>

          {/* Eget uträknat */}
          {Object.keys(computedMap).length > 0 && (
            <>
              <div className="font-medium text-[10px] text-blue-700 py-1">Eget uträknat</div>
              <div className="grid gap-1 py-1" style={{ gridTemplateColumns: `repeat(${terms.length}, 1fr)` }}>
                {terms.map((t) => {
                  const cv = computedMap[t];
                  const hasOfficial = officialMap[t] != null;
                  if (cv == null || hasOfficial) {
                    return <div key={t} className="text-center text-[10px] text-gray-300">–</div>;
                  }
                  return (
                    <div
                      key={t}
                      className="text-center text-[10px] font-semibold rounded text-blue-800 bg-blue-50 border border-blue-200 py-0.5"
                    >
                      {cv.toFixed(1)}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        <p className="text-[10px] text-gray-400 mt-3">
          Betyg i parentes <span className="font-semibold text-blue-700">(C)</span> = ärvt från föregående termin.
          &quot;Eget uträknat&quot; är beräknat enligt Skolverket (16 bästa + alla moderna språk).
        </p>
      </div>

      {/* Trend mellan terminer — på sida 1 om plats finns */}
      {trendData.length > 0 && (
        <div className="rounded-xl border border-gray-200 bg-white p-3 mb-4 card-print" data-testid="grades-print-trend">
          <p className="text-xs uppercase tracking-wider text-gray-500 mb-2">
            Trend mellan terminer
          </p>
          <ul className="space-y-2 text-xs">
            {trendData.map((t) => (
              <li key={`${t.from}-${t.to}`} className="flex items-center gap-2 flex-wrap">
                <span className="font-medium text-gray-700 min-w-[110px]">
                  {fmtTerm(t.from)} → {fmtTerm(t.to)}
                </span>
                <span className="inline-flex items-center gap-1 text-emerald-700" title={t.upList.join("\n") || "Inga"}>
                  <TrendingUp size={12} /> {t.up} höjda
                </span>
                <span className="inline-flex items-center gap-1 text-red-700" title={t.downList.join("\n") || "Inga"}>
                  <TrendingDown size={12} /> {t.down} sänkta
                </span>
                <span className="inline-flex items-center gap-1 text-gray-600">
                  <Minus size={12} /> {t.same} oförändrade
                </span>
              </li>
            ))}
          </ul>
          <p className="text-[10px] text-gray-400 mt-2">
            Endast ämnen med betyg i båda terminerna räknas.
          </p>
        </div>
      )}

      {/* Betyg per ämne & termin — tvinga ny sida vid utskrift */}
      <div
        className="rounded-xl border border-gray-200 bg-white p-3 mb-4 card-print page-break-before"
        data-testid="grades-print-chart"
      >
        <p className="text-xs uppercase tracking-wider text-gray-500 mb-1">
          Betyg per ämne &amp; termin
        </p>
        <p className="text-[9px] text-gray-500 mb-2">
          Y = ämne, X = betyg (F → A). En stapel per termin med betyget markerat.
        </p>
        {(() => {
          // Adaptiv dimensionering: cappa chart-höjden vid ~920px så det
          // ALLTID får plats på A4-sida 2 (12mm marginal → ~1030px usable;
          // minus heading/legend/padding ≈ 110px).
          const A4_BUDGET = 920;
          const idealPerSubject = Math.max(28, terms.length * 7 + 14);
          const naiveHeight = subjects.length * idealPerSubject;
          const chartHeight = Math.max(280, Math.min(A4_BUDGET, naiveHeight));
          const perSubject = subjects.length > 0 ? chartHeight / subjects.length : 0;
          // Beräkna inre bar-höjd för att veta om labels får plats
          const barCategoryGap = perSubject > 50 ? 4 : (perSubject > 35 ? 2 : 1);
          const barGap = perSubject > 50 ? 2 : (perSubject > 35 ? 1 : 0);
          // EXPLICIT barSize säkerställer att rader får SAMMA visuella höjd
          // även när vissa ämnen saknar betyg för en termin (recharts skulle
          // annars auto-skala och göra rader ojämna).
          const barHeight = terms.length > 0
            ? Math.max(3, (perSubject - barCategoryGap - (terms.length - 1) * barGap) / terms.length)
            : 6;
          const showLabels = barHeight >= 7;
          const yAxisFont = subjects.length > 20 ? 8 : 9;
          const xAxisFont = subjects.length > 20 ? 9 : 10;
          const yAxisWidth = subjects.length > 20 ? 78 : 84;
          return (
            <div style={{ width: "100%", height: chartHeight }}>
              <ResponsiveContainer>
                <BarChart
                  data={chartData}
                  layout="vertical"
                  margin={{ top: 2, right: 14, left: 0, bottom: 2 }}
                  barCategoryGap={barCategoryGap}
                  barGap={barGap}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" horizontal={false} />
                  <XAxis
                    type="number"
                    domain={[0, 6]}
                    ticks={[1, 2, 3, 4, 5, 6]}
                    tickFormatter={(v) => NUM_GRADE[v] || ""}
                    tick={{ fontSize: xAxisFont, fontWeight: 600 }}
                  />
                  <YAxis
                    type="category"
                    dataKey="name"
                    tick={{ fontSize: yAxisFont }}
                    width={yAxisWidth}
                    interval={0}
                  />
                  {terms.map((t, idx) => (
                    <Bar
                      key={t}
                      dataKey={t}
                      fill={TERM_COLORS[idx % TERM_COLORS.length]}
                      radius={[0, 2, 2, 0]}
                      isAnimationActive={false}
                      barSize={barHeight}
                    >
                      {/* Per-cell Cell: visa fullt färgad bar när termin
                          finns, mycket svag grå (~5% opacitet) när termin
                          saknas — slot:en bevaras så rader blir uniformt
                          höga oavsett om någon termin har data eller ej. */}
                      {chartData.map((entry, i) => (
                        <Cell
                          key={`c-${i}`}
                          fill={entry[`${t}_present`] ? TERM_COLORS[idx % TERM_COLORS.length] : "#e5e7eb"}
                          fillOpacity={entry[`${t}_present`] ? 1 : 0.25}
                          stroke="none"
                        />
                      ))}
                      {showLabels && (
                        <LabelList
                          dataKey={`${t}_label`}
                          position="insideRight"
                          style={{ fill: "white", fontSize: Math.max(6, Math.min(8, barHeight - 1)), fontWeight: 600 }}
                        />
                      )}
                    </Bar>
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>
          );
        })()}

        {/* Legend */}
        <div className="flex flex-wrap gap-x-2 gap-y-0.5 justify-center text-[9px] text-gray-600 mt-1 pt-1 border-t border-gray-200">
          {terms.map((t, idx) => (
            <span key={t} className="inline-flex items-center gap-1">
              <span
                className="inline-block w-2 h-2 rounded-sm"
                style={{ backgroundColor: TERM_COLORS[idx % TERM_COLORS.length] }}
              />
              {fmtTerm(t)}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
